import { isRunningInMobileApp } from '@sportschool/shared-logic';
import { NavigationLocationTarget } from '@/lib/utils';

export type NavigationProvider = 'google' | 'apple' | 'waze';

const STORAGE_KEY = 'preferred_navigation_provider';

export function getPreferredNavigationProvider(): NavigationProvider | null {
  try {
    const val = localStorage.getItem(STORAGE_KEY);
    if (val === 'google' || val === 'apple' || val === 'waze') {
      return val;
    }
    return null;
  } catch {
    return null;
  }
}

export function setPreferredNavigationProvider(provider: NavigationProvider | null) {
  try {
    if (provider) {
      localStorage.setItem(STORAGE_KEY, provider);
    } else {
      localStorage.removeItem(STORAGE_KEY);
    }
  } catch {
    // Ignore storage errors
  }
}

export function getNavigationUrlForProvider(
  provider: NavigationProvider,
  location?: NavigationLocationTarget | null
): string {
  if (!location) return 'https://www.google.com/maps';

  const hasCoords =
    typeof location.latitude === 'number' &&
    typeof location.longitude === 'number' &&
    !isNaN(location.latitude) &&
    !isNaN(location.longitude);
  const coordsStr = hasCoords ? `${location.latitude},${location.longitude}` : '';

  const addressParts = [
    location.name,
    location.address,
    location.district,
    location.city,
  ].filter(Boolean);
  const queryStr = addressParts.join(', ') || location.name || 'Lokalizacja';

  switch (provider) {
    case 'google':
      return hasCoords
        ? `https://www.google.com/maps/dir/?api=1&destination=${coordsStr}`
        : `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(queryStr)}`;
    case 'apple':
      return hasCoords
        ? `https://maps.apple.com/?daddr=${coordsStr}&q=${encodeURIComponent(location.name || queryStr)}`
        : `https://maps.apple.com/?daddr=${encodeURIComponent(queryStr)}`;
    case 'waze':
      return hasCoords
        ? `https://waze.com/ul?ll=${coordsStr}&navigate=yes`
        : `https://waze.com/ul?q=${encodeURIComponent(queryStr)}&navigate=yes`;
    default:
      return `https://www.google.com/maps/dir/?api=1&destination=${hasCoords ? coordsStr : encodeURIComponent(queryStr)}`;
  }
}

export function openNavigation(
  provider: NavigationProvider,
  location?: NavigationLocationTarget | null
) {
  const url = getNavigationUrlForProvider(provider, location);

  // If running inside the mobile app WebView, navigate directly so App.tsx intercepts and launches native GPS navigation apps
  if (isRunningInMobileApp()) {
    window.location.href = url;
  } else {
    window.open(url, '_blank', 'noopener,noreferrer');
  }
}
